import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const OrderConfirmation = () => {
  const { id } = useParams();
  const { apiRequest } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await apiRequest(`/orders/${id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }

        const data = await response.json();
        setOrder(data.data.order);
      } catch (err) {
        console.error('Order fetch error:', err);
        setError(err.message);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, apiRequest]);

  if (loading) {
    return (
      <div className="bg-softRose min-h-screen py-12">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-slateText mb-4">Loading Order Details...</h2>
          <p className="text-gray-600">Please wait while we fetch your order information</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-softRose min-h-screen py-12">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-slateText mb-4">Order Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The requested order could not be found.'}</p>
          <Link 
            to="/products" 
            className="inline-block px-6 py-3 bg-pinkAccent text-white rounded-lg hover:bg-pinkAccent/90 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-softRose min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-lg text-slateText">
            Thank you for your purchase, {order.customerInfo.name}!
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Order #{order._id} • {new Date(order.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-6 pb-2 border-b border-lightGray">
              Order Details
            </h2>

            {/* Shipping Info */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-slateText uppercase tracking-wider mb-3">
                Shipping Information
              </h3>
              <div className="space-y-1 text-sm">
                <p className="text-gray-900">{order.customerInfo.name}</p>
                <p className="text-gray-900">{order.shippingAddress.street}</p>
                <p className="text-gray-900">
                  {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                </p>
                <p className="text-gray-900">{order.shippingAddress.country}</p>
                <p className="text-gray-900 mt-2">{order.customerInfo.phone}</p>
                <p className="text-gray-900">{order.customerInfo.email}</p>
              </div>
            </div>

            {/* Payment Info */}
            <div>
              <h3 className="text-sm font-medium text-slateText uppercase tracking-wider mb-3">
                Payment Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="text-gray-900 font-medium capitalize">
                    {order.paymentMethod}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className="text-gray-900 font-medium capitalize">
                    {order.paymentStatus}
                  </span>
                </div>
                {order.paidAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid On:</span>
                    <span className="text-gray-900">
                      {new Date(order.paidAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 h-fit sticky top-4">
            <h2 className="text-xl font-medium text-gray-900 mb-6 pb-2 border-b border-lightGray">
              Order Summary
            </h2>

            {/* Items List */}
            <div className="space-y-4 mb-6">
              {order.items.map((item) => (
                <div key={item._id} className="flex items-start space-x-4">
                  <img
                    src={item.image || '/placeholder-product.jpg'}
                    alt={item.name}
                    className="w-16 h-16 rounded-md object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-slateText">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            {/* Order Totals */}
            <div className="space-y-3 pt-4 border-t border-lightGray">
              <div className="flex justify-between">
                <span className="text-slateText">Subtotal</span>
                <span className="text-gray-900">${order.itemsPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slateText">Shipping</span>
                <span className="text-gray-900">
                  {order.shippingPrice === 0 ? 'Free' : `$${order.shippingPrice.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slateText">Tax</span>
                <span className="text-gray-900">${order.taxPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-medium pt-3 border-t border-lightGray">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">${order.totalPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* Order Notes */}
            {order.notes && (
              <div className="mt-6 pt-4 border-t border-lightGray">
                <h3 className="text-sm font-medium text-slateText mb-2">Order Notes</h3>
                <p className="text-sm text-gray-600 italic">{order.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 space-y-3">
              <Link
                to="/products"
                className="block w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-center text-pinkAccent bg-pinkAccent/10 hover:bg-pinkAccent/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pinkAccent"
              >
                Continue Shopping
              </Link>
              <Link
                to={`/orders/${order._id}/track`}
                className="block w-full px-4 py-2 border border-lightGray rounded-md shadow-sm text-sm font-medium text-center text-slateText bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pinkAccent"
              >
                Track Your Order
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;